import React from "react";
import { useApp } from "@/context/AppContext";
import { X } from "lucide-react";
import { embedLabel } from "@/lib/linkText";
import { resolveMediaUrl } from "@/lib/mediaUrl";

// WhatsApp-style link preview card. Used by the Story composer (with `onRemove`)
// and the Story viewer (clickable, opens the link). `embed` is the shape returned
// by GET /api/link-preview: { kind, url, domain, title, description, thumbnail,
// provider, author }.
export { embedLabel };

export const LinkPreviewCard = ({ embed, onRemove, testId = "link-preview-card", compact = false, large = false }) => {
  const { t } = useApp();
  if (!embed || !embed.url) return null;
  const label = embedLabel(embed);
  const title = embed.title || embed.url;
  const inner = (
    <>
      {embed.thumbnail && (
        <img
          src={resolveMediaUrl(embed.thumbnail)}
          alt=""
          loading="lazy"
          decoding="async"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className={`w-full ${compact ? "max-h-28" : "aspect-video"} object-cover`}
          data-testid={`${testId}-image`}
        />
      )}
      <div className={`${large ? "px-4 py-3" : "px-3 py-2"} text-left`}>
        {label && (
          <div className={`${large ? "text-[11px]" : "text-[10px]"} font-semibold uppercase tracking-wide truncate`} style={{ color: "var(--yalola-text-muted)" }}>
            {label}
          </div>
        )}
        <div className={`${large ? "text-base line-clamp-3" : "text-sm line-clamp-2"} font-semibold leading-snug`} style={{ overflowWrap: "anywhere" }} data-testid={`${testId}-title`}>{title}</div>
        {embed.author && !embed.description && (
          <div className={`${large ? "text-sm" : "text-xs"} truncate mt-0.5`} style={{ color: "var(--yalola-text-muted)" }}>{embed.author}</div>
        )}
        {embed.description && (
          <div className={`${large ? "text-sm line-clamp-3" : "text-xs line-clamp-2"} mt-0.5`} style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-description`}>
            {embed.description}
          </div>
        )}
      </div>
    </>
  );

  // Composer variant: static card with a remove button.
  if (onRemove) {
    return (
      <div className="relative rounded-2xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={testId}>
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full grid place-items-center text-white transition-transform hover:scale-110"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          aria-label={t("link.remove_preview", "Retirer l'aperçu")}
          data-testid={`${testId}-remove`}
        >
          <X size={15} />
        </button>
        {inner}
      </div>
    );
  }

  // Viewer variant: the whole card opens the link.
  return (
    <a
      href={embed.url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="block rounded-2xl overflow-hidden border transition-opacity hover:opacity-95"
      style={{ borderColor: "rgba(255,255,255,0.25)", background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(6px)" }}
      data-testid={testId}
    >
      {inner}
    </a>
  );
};

export default LinkPreviewCard;
